import React from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Skeleton, useTheme } from '@mui/material';
import CopyButton from '../common/CopyButton';
import { formatTimeToIST, formatDateAsSent } from '../../../utils/dateUtils';
import { formatPnlInr, formatRateInr } from './pnlFormatters';

// Loading-placeholder widths per column (not the column widths), sized like real
// content so the layout barely moves when data arrives.
const SKELETON_WIDTHS = [
  140, // Bill ID + copy button
  110, // Symbol
  36,  // Exchange
  72,  // Date
  60,  // Time
  32,  // Quantity
  56,  // Buy Avg
  56,  // Sell Avg
  64,  // Realized PnL
];
const SKELETON_ROW_COUNT = 12;

/** PnL rows as a sticky-header table, used at `sm` and above. */
const PnlHistoryTable = ({ pnlHistory, loading = false }) => {
  const theme = useTheme();

  // Colour alone carries the sign in this table, so it lives with the table.
  const getPnlColor = (value) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return theme.palette.text.secondary;
    return numValue > 0 ? theme.palette.success.main : numValue < 0 ? theme.palette.error.main : theme.palette.text.secondary;
  };

  return (
                <Table 
                  stickyHeader 
                  aria-label="sticky table" 
                  aria-busy={loading || undefined}
                  size="small"
                  sx={{
                    tableLayout: 'auto',
                    // Same layout as Trade History: centred values that never
                    // wrap, 8px cell padding (16px at the outer edges), so all
                    // nine columns fit narrower panes before scrolling sideways.
                    '& th, & td': { textAlign: 'center', whiteSpace: 'nowrap', px: 1 },
                    '& th:last-of-type, & td:last-of-type': { pr: 2 },
                    // Bill ID: left-aligned, and with auto layout a 1% column
                    // shrinks to its content; the other columns share the rest.
                    '& th:first-of-type, & td:first-of-type': { textAlign: 'left', pl: 2, width: '1%' },
                  }}
                >
                  <TableHead sx={{
                    '& th': {
                      position: 'sticky',
                      top: 0,
                      zIndex: 10,
                      whiteSpace: 'nowrap',
                      boxShadow: '0px 2px 2px -1px rgba(0,0,0,0.1)'
                    }
                  }}>
                    <TableRow>
                      <TableCell>Bill ID</TableCell>
                      <TableCell>Symbol</TableCell>
                      <TableCell>Exchange</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Time</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Buy Avg</TableCell>
                      <TableCell>Sell Avg</TableCell>
                      <TableCell>Realized PnL</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading && Array.from({ length: SKELETON_ROW_COUNT }, (_, rowIndex) => (
                      <TableRow key={`skeleton-${rowIndex}`}>
                        {SKELETON_WIDTHS.map((width, colIndex) => (
                          <TableCell key={colIndex}>
                            {/* Skeletons are blocks, so text-align can't place them — margins do. */}
                            <Skeleton variant="text" width={width} sx={{ mx: colIndex === 0 ? 0 : 'auto' }} />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                    {!loading && pnlHistory.map((item) => (
                      <TableRow hover tabIndex={-1} key={item.bill_id}>
                        <TableCell>
                          {/* Tabular digits give every ID of the same length the same
                              width, so the copy icons line up down the column. */}
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                            {item.bill_id}
                            {item.bill_id && <CopyButton value={item.bill_id} label="bill ID" />}
                          </Box>
                        </TableCell>
                        <TableCell>{item.trading_symbol || item.instrument_id}</TableCell>
                        <TableCell>{item.exchange}</TableCell>
                        {/* IST wall-clock as sent, like the mobile cards. */}
                        <TableCell>{formatDateAsSent(item.trade_time) || '-'}</TableCell>
                        <TableCell>{formatTimeToIST(item.trade_time) || '-'}</TableCell>
                        <TableCell>{item.qty}</TableCell>
                        <TableCell>{item.buy_avg > 0 ? formatRateInr(item.buy_avg) : "-"}</TableCell>
                        <TableCell>{item.sell_avg > 0 ? formatRateInr(item.sell_avg) : "-"}</TableCell>
                        {/* Colour alone carries the sign here; the text inherits
                            the table's own size and weight. */}
                        <TableCell sx={{ color: getPnlColor(item.realized_pnl) }}>
                          {formatPnlInr(item.realized_pnl)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
  );
};

export default PnlHistoryTable;
